/// <reference path="../types/office.d.ts" />

import { ParsedContact, OutlookColor, OUTLOOK_COLORS } from '@/types/contact';
import { supabase } from '@/integrations/supabase/client';

/**
 * Microsoft Graph API service
 * Handles contact creation and duplicate checking via server-side proxy
 * All Graph API requests are proxied through secure Edge Functions
 */
export class GraphService {
  /**
   * Convert OutlookColor name to Graph API preset code
   * e.g. "Blue" → "preset7", "Orange" → "preset1"
   */
  private static colorToPreset(color: OutlookColor): string {
    const colorDef = OUTLOOK_COLORS.find(c => c.name === color);
    return colorDef?.preset || 'preset7'; // Fallback to Blue
  }
  /**
   * Make proxied request to Microsoft Graph API
   * All tokens are stored server-side for security
   */
  private static async graphRequest(endpoint: string, method: string = 'GET', body?: any): Promise<any> {
    console.log('[GraphService] ========================================');
    console.log('[GraphService] === GRAPH API REQUEST VIA PROXY ===');
    console.log('[GraphService] ========================================');
    console.log('[GraphService] Endpoint:', endpoint);
    console.log('[GraphService] Method:', method);

    const { data, error } = await supabase.functions.invoke('graph-proxy', {
      body: {
        endpoint,
        method,
        body,
      },
    });

    if (error) {
      console.error('[GraphService] Proxy error:', error);
      console.error('[GraphService] Error details:', {
        message: error.message,
        context: error.context,
        name: error.name,
      });
      
      // First: Try to get the actual error from the Edge Function response
      let userMessage: string | null = null;
      
      // FunctionsHttpError: context is a Response object, need to call .json()
      try {
        if (error.context && typeof error.context.json === 'function') {
          const responseBody = await error.context.json();
          console.log('[GraphService] Parsed error response:', responseBody);
          if (responseBody.error && typeof responseBody.error === 'string') {
            userMessage = responseBody.error;
            console.log('[GraphService] Got error from context:', userMessage);
          }
          // Append code + nested Graph message when present (better diagnostics)
          const code = responseBody.code;
          const graphMsg = responseBody.details?.error?.message;
          if (userMessage && graphMsg && !userMessage.includes(graphMsg)) {
            userMessage = `${userMessage} [${code ?? 'GRAPH'}: ${graphMsg}]`;
          } else if (!userMessage && graphMsg) {
            userMessage = `Microsoft Graph API error${code ? ` (${code})` : ''}: ${graphMsg}`;
          }
        }
      } catch (parseError) {
        console.warn('[GraphService] Could not parse error context:', parseError);
      }
      
      // If no message from response, check the error message itself
      if (!userMessage) {
        const errorMsg = error.message || '';
        console.log('[GraphService] Checking error message:', errorMsg);
        
        if (errorMsg.includes('token expired') || errorMsg.includes('Token expired')) {
          userMessage = 'Microsoft authentication expired. Please sign in again.';
        } else if (errorMsg.includes('No Microsoft Graph token')) {
          userMessage = 'Microsoft authentication required. Please sign in again.';
        } else if (errorMsg.includes('Unauthorized') || errorMsg.includes('401')) {
          userMessage = 'Not authorized. Please sign in again.';
        } else if (errorMsg.includes('Permission denied') || errorMsg.includes('403')) {
          userMessage = 'Permission denied. Please re-authenticate with the required permissions.';
        } else {
          // Generic fallback - only as last resort
          userMessage = 'Failed to connect to Microsoft. Please try again.';
        }
      }
      
      console.log('[GraphService] Final error message:', userMessage);
      throw new Error(userMessage);
    }

    return data;
  }

  /**
   * Strip control characters and trim. Graph API rejects payloads with
   * stray control chars (0x00-0x1F, 0x7F) with UnableToDeserializePostBody.
   */
  private static sanitizeString(value: unknown): string {
    if (value === undefined || value === null) return '';
    const str = typeof value === 'string' ? value : String(value);
    // Remove control chars but keep \t \n \r which JSON handles fine
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').trim();
  }

  /**
   * Sanitize an address object: only allow Graph-supported string fields.
   */
  private static sanitizeAddress(addr: any): Record<string, string> | undefined {
    if (!addr || typeof addr !== 'object') return undefined;
    const allowed = ['street', 'city', 'postalCode', 'state', 'countryOrRegion'];
    const out: Record<string, string> = {};
    for (const k of allowed) {
      const v = this.sanitizeString(addr[k]);
      if (v) out[k] = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  /**
   * Sanitize an array of phone strings.
   */
  private static sanitizePhones(arr: unknown): string[] | undefined {
    if (!Array.isArray(arr)) return undefined;
    const out = arr
      .map((p) => this.sanitizeString(p))
      .filter((p) => p.length > 0)
      .slice(0, 10); // Graph practical limit
    return out.length > 0 ? out : undefined;
  }

  /**
   * Helper function to clean empty values from contact data.
   * Recursively removes undefined/null/empty values and sanitizes strings.
   */
  private static cleanContactData(data: any): any {
    const cleaned: any = {};

    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === null) continue;

      if (typeof value === 'string') {
        const s = this.sanitizeString(value);
        if (s) cleaned[key] = s;
        continue;
      }

      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        // Array of strings -> sanitize each
        if (value.every((v) => typeof v === 'string')) {
          const arr = (value as string[])
            .map((v) => this.sanitizeString(v))
            .filter((v) => v.length > 0);
          if (arr.length > 0) cleaned[key] = arr;
          continue;
        }
        // Array of objects (e.g. emailAddresses) -> recurse, drop empties
        const arr = value
          .map((v) => (v && typeof v === 'object' ? this.cleanContactData(v) : v))
          .filter((v) => v !== undefined && v !== null && !(typeof v === 'object' && Object.keys(v).length === 0));
        if (arr.length > 0) cleaned[key] = arr;
        continue;
      }

      if (typeof value === 'object') {
        const nested = this.cleanContactData(value);
        if (Object.keys(nested).length > 0) cleaned[key] = nested;
        continue;
      }

      cleaned[key] = value;
    }

    return cleaned;
  }

  /**
   * Create contact via Graph API (proxied)
   */
  static async createContact(contact: ParsedContact & { notes?: string }): Promise<void> {
    // Ensure category exists with correct color BEFORE creating contact
    // Always call ensureCategory if a category name exists (with Blue as final fallback)
    if (contact.categories && contact.categories.length > 0) {
      console.log('[GraphService] 🏷️ createContact - calling ensureCategory:', {
        categoryName: contact.categories[0],
        categoryColor: contact.categoryColor,
        willUseFallback: !contact.categoryColor
      });
      await this.ensureCategory(contact.categories[0], contact.categoryColor || 'Blue');
    }

    // Determine display name
    const displayName = contact.displayName || 
      [contact.givenName, contact.middleName, contact.surname]
        .filter(Boolean)
        .join(' ') || 
      contact.companyName || '';

    // Microsoft Graph's contact schema does NOT support most legacy MAPI phone/fax
    // fields (businessFax, homeFax, carPhone, pager, callback, radioPhone, telex,
    // companyMainPhone, assistantPhone, otherPhones, personalWebPage, anniversary,
    // schools, hobby, location). Sending any of them causes a
    // 400 UnableToDeserializePostBody and the contact is not created.
    // We preserve fax numbers by appending them to businessPhones / homePhones
    // with a "Fax:" prefix so the data is not lost.
    // See: https://learn.microsoft.com/en-us/graph/api/resources/contact

    const displayNameClean = this.sanitizeString(displayName);

    const businessPhones = [
      ...((contact.businessPhones || []).filter((p) => p) as string[]),
      ...(contact.businessFax ? [`Fax: ${contact.businessFax}`] : []),
    ];
    const homePhones = [
      ...((contact.homePhones || []).filter((p) => p) as string[]),
      ...(contact.homeFax ? [`Fax: ${contact.homeFax}`] : []),
      ...(contact.otherFax ? [`Fax: ${contact.otherFax}`] : []),
    ];

    // Build Graph API contact object with only fields supported by the schema
    const rawContact: any = {
      // Name
      title: contact.title,
      givenName: contact.givenName,
      middleName: contact.middleName,
      surname: contact.surname,
      suffix: contact.suffix,
      nickName: contact.nickname, // Graph uses "nickName" (capital N)
      displayName: displayNameClean,
      yomiGivenName: contact.yomiGivenName,
      yomiSurname: contact.yomiSurname,

      // Business
      companyName: contact.companyName,
      yomiCompanyName: contact.yomiCompanyName,
      department: contact.department,
      jobTitle: contact.jobTitle,
      profession: contact.profession,
      officeLocation: contact.officeLocation,
      manager: contact.manager,
      assistantName: contact.assistantName,

      // Email - normalized to {address, name} pairs, only with valid address strings
      emailAddresses: (contact.emailAddresses || [])
        .map((email) => {
          const address = this.sanitizeString(email);
          if (!address) return null;
          return { address, name: displayNameClean || address };
        })
        .filter((e): e is { address: string; name: string } => e !== null),

      // Phones (only schema-supported fields; fax numbers folded in above)
      businessPhones: this.sanitizePhones(businessPhones),
      homePhones: this.sanitizePhones(homePhones),
      mobilePhone: this.sanitizeString(contact.mobilePhone),

      // Web
      businessHomePage: contact.businessHomePage,

      // IM
      imAddresses: this.sanitizePhones(contact.imAddresses),

      // Addresses - strictly only Graph-supported keys
      businessAddress: this.sanitizeAddress(contact.businessAddress),
      homeAddress: this.sanitizeAddress(contact.homeAddress),
      otherAddress: this.sanitizeAddress(contact.otherAddress),

      // Personal
      spouseName: contact.spouseName,
      generation: contact.generation,
      birthday: contact.birthday,

      // Meta
      personalNotes: contact.notes || undefined,
      categories: contact.categories && contact.categories.length > 0
        ? contact.categories
        : undefined,
    };


    // Clean the contact data to remove empty values
    const graphContact = this.cleanContactData(rawContact);

    try {
      const data = await this.graphRequest('/me/contacts', 'POST', graphContact);
      console.log('[GraphService] Contact created:', data?.id);
    } catch (error) {
      console.error('[GraphService] Failed to create contact:', error);
      throw error;
    }
  }

  /**
   * Check for duplicate contact by email
   */
  static async checkDuplicate(email: string): Promise<boolean> {
    const emailQuery = encodeURIComponent(email);
    const endpoint = `/me/contacts?$filter=emailAddresses/any(e:e/address eq '${emailQuery}')&$select=id`;
    
    try {
      const data = await this.graphRequest(endpoint, 'GET');
      return data.value && data.value.length > 0;
    } catch (error) {
      console.error('[GraphService] Failed to check duplicate:', error);
      throw new Error('Could not verify existing contacts. Please try again.');
    }
  }

  /**
   * Get all Outlook categories
   */
  static async getCategories(): Promise<Array<{ id: string; displayName: string; color: string }>> {
    try {
      const data = await this.graphRequest('/me/outlook/masterCategories', 'GET');
      return data.value || [];
    } catch (error) {
      console.error('[GraphService] Failed to get categories:', error);
      throw new Error('Failed to retrieve Outlook categories');
    }
  }

  /**
   * Ensure category exists with correct color
   */
  static async ensureCategory(categoryName: string, color: OutlookColor): Promise<void> {
    // Calculate preset early for logging
    const colorPreset = this.colorToPreset(color || 'Blue');
    const expectedColorDef = OUTLOOK_COLORS.find(c => c.preset === colorPreset);
    
    console.log('[GraphService] 🏷️ ensureCategory - Color Mapping:', {
      inputColor: color,
      mappedPreset: colorPreset,
      expectedOutlookColor: expectedColorDef?.name,
      colorIsUndefined: color === undefined,
      colorIsNull: color === null
    });
    
    // Validate inputs
    if (!categoryName || categoryName.trim() === '') {
      console.error('[GraphService] ❌ Category name is empty');
      throw new Error('Category name cannot be empty');
    }
    
    if (!color) {
      console.warn('[GraphService] ⚠️ No color specified, using default Blue');
      color = 'Blue';
    }
    
    try {
      // Get all existing categories
      const categoriesData = await this.graphRequest('/me/outlook/masterCategories', 'GET');
      const existingCategory = categoriesData.value.find(
        (cat: any) => cat.displayName === categoryName
      );

      if (existingCategory) {
        console.log('[GraphService] Category exists:', existingCategory.displayName, 'with color:', existingCategory.color);
        
        // Check if color needs updating (compare preset codes)
        const expectedPreset = this.colorToPreset(color);
        if (existingCategory.color !== expectedPreset) {
          console.log('[GraphService] ⚠️  Color mismatch! Current:', existingCategory.color, 'Expected:', color);
          console.log('[GraphService] Deleting and recreating category with correct color...');
          
          // Delete the category with wrong color
          try {
            await this.graphRequest(`/me/outlook/masterCategories/${existingCategory.id}`, 'DELETE');
            console.log('[GraphService] ✅ Old category deleted');
          } catch (deleteError: any) {
            if (!deleteError.message.includes('404')) {
              console.error('[GraphService] ❌ Failed to delete category:', deleteError);
              throw new Error('Failed to delete category');
            }
          }
          
          // Create category with correct color (convert to preset)
          const presetColor = this.colorToPreset(color);
          const createdCategory = await this.graphRequest('/me/outlook/masterCategories', 'POST', {
            displayName: categoryName,
            color: presetColor,
          });
          console.log('[GraphService] ✅ Category recreated with correct color:', createdCategory.displayName, createdCategory.color);
        } else {
          console.log('[GraphService] ✅ Category color is already correct');
        }
      } else {
        console.log('[GraphService] Category does not exist, creating...');
        
        // Create new category
        try {
          // Convert color name to Graph API preset code
          const presetColor = this.colorToPreset(color);
          const createdCategory = await this.graphRequest('/me/outlook/masterCategories', 'POST', {
            displayName: categoryName,
            color: presetColor,
          });
          console.log('[GraphService] ✅ Category created:', createdCategory.displayName, createdCategory.color);
        } catch (createError: any) {
          const errorMessage = createError.message || '';
          
          // Log full error for debugging
          console.error('[GraphService] ❌ Category creation error details:', {
            message: errorMessage,
            categoryName,
            color,
            fullError: createError,
          });
          
          // Check for specific error types
          if (errorMessage.includes('PERMISSION') || errorMessage.includes('Permission denied') || errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
            console.error('[GraphService] ❌ PERMISSION DENIED - Missing Mailbox.ReadWrite permission');
            throw new Error(
              'Missing permission to create Outlook categories. Please re-authenticate with the required permissions.'
            );
          } else if (errorMessage.includes('already exists') || errorMessage.includes('409')) {
            console.log('[GraphService] 409 - Kategorie existiert bereits, prüfe Farbe...');
            
            // Fetch existing category and check/fix color
            try {
              const categoriesData = await this.graphRequest('/me/outlook/masterCategories', 'GET');
              const existingCat = categoriesData.value.find(
                (cat: any) => cat.displayName === categoryName
              );
              
              if (existingCat && existingCat.color !== colorPreset) {
                console.log('[GraphService] ⚠️ Korrigiere Farbe via PATCH:', {
                  aktuell: existingCat.color,
                  erwartet: colorPreset
                });
                await this.graphRequest(
                  `/me/outlook/masterCategories/${encodeURIComponent(existingCat.id)}`,
                  'PATCH',
                  { color: colorPreset }
                );
                console.log('[GraphService] ✅ Farbe korrigiert');
              } else {
                console.log('[GraphService] ✅ Farbe ist bereits korrekt:', existingCat?.color);
              }
            } catch (patchError) {
              console.warn('[GraphService] Farb-Korrektur fehlgeschlagen:', patchError);
            }
            return;
          } else if (errorMessage.includes('authentication') || errorMessage.includes('expired') || errorMessage.includes('401')) {
            throw new Error('Microsoft authentication expired. Please sign in again.');
          } else if (errorMessage.includes('not found') || errorMessage.includes('404')) {
            // 404 on POST is very unusual - likely a token problem
            throw new Error('Microsoft connection error. Please sign in again.');
          } else if (errorMessage.includes('rate') || errorMessage.includes('throttl') || errorMessage.includes('429')) {
            throw new Error('Too many requests to Microsoft. Please wait a moment and try again.');
          }
          
          // For other errors, include more context in the message
          console.error('[GraphService] Category creation failed:', createError);
          throw new Error(`Could not create Outlook category "${categoryName}". Error: ${errorMessage.substring(0, 100)}`);
        }
      }
    } catch (error) {
      console.error('[GraphService] Error ensuring category:', error);
      throw error;
    }
  }
}
